import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { normalizeTime, formatTyping, splitDateTime, joinDateTime } from '../../utils/time24';

/**
 * Ô nhập GIỜ 24h ("HH:mm"), thay cho <input type="time">.
 *
 * <input type="time"> hiển thị theo cài đặt vùng của hệ điều hành (máy để English/US ra
 * "02:30 PM") và không có thuộc tính nào ép 24h, nên mọi ô giờ trong hệ thống dùng component này.
 *
 * Giữ nguyên hợp đồng của ô gốc để thay thế không phải sửa logic từng trang:
 *   • value: 'HH:mm' (hoặc 'HH:mm:ss' — bỏ giây); '' = chưa có giờ.
 *   • onChange(e) với e.target.value = 'HH:mm' khi gõ đủ & hợp lệ, '' khi xoá trắng.
 *     (e.target còn có name/id để handler dùng chung kiểu `[e.target.name]: e.target.value` chạy được.)
 * Gõ tự nhiên: "0830" → 08:30, "8h30", "8:30"; rời ô thì chuẩn hoá, giờ sai thì quay về giá trị trước.
 */
export const TimeInput24 = forwardRef(function TimeInput24(
  {
    value, onChange, onBlur, name, id, className, disabled, readOnly, required, autoFocus,
    placeholder = 'HH:mm', title, 'aria-label': ariaLabel, 'data-testid': testId,
  },
  ref
) {
  const committed = normalizeTime(value) || '';
  const [text, setText] = useState(committed);
  const typing = useRef(false);

  // Giá trị đổi từ bên ngoài (nạp form, đổi phiên…) → cập nhật ô, trừ khi người dùng đang gõ dở.
  useEffect(() => {
    if (!typing.current) setText(committed);
  }, [committed]);

  const emit = (v) => {
    if (typeof onChange === 'function') onChange({ target: { name, id, value: v }, currentTarget: { name, id, value: v } });
  };

  const handleChange = (e) => {
    typing.current = true;
    const t = formatTyping(e.target.value);
    setText(t);
    if (t === '') { emit(''); return; }
    // Chỉ báo ra ngoài khi đã đủ "HH:mm" hợp lệ; gõ dở thì giữ ở ô, chưa làm bẩn state của trang.
    if (/^\d{2}:\d{2}$/.test(t)) {
      const ok = normalizeTime(t);
      if (ok) emit(ok);
    }
  };

  const handleBlur = (e) => {
    typing.current = false;
    const raw = e.target.value;
    if (raw === '') { setText(''); } else {
      const ok = normalizeTime(raw);
      if (ok) { setText(ok); if (ok !== committed) emit(ok); } else { setText(committed); }
    }
    if (typeof onBlur === 'function') onBlur(e);
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      name={name}
      id={id}
      value={text}
      placeholder={placeholder}
      title={title || 'Giờ 24h, ví dụ 07:30 hoặc 17:45'}
      aria-label={ariaLabel}
      data-testid={testId}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      autoFocus={autoFocus}
      className={className}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});

/**
 * Ô nhập NGÀY + GIỜ 24h, thay cho <input type="datetime-local">.
 *
 * value: 'YYYY-MM-DDTHH:mm' hoặc ''. Phần NGÀY luôn có sẵn: nếu value rỗng thì tự điền `defaultDate`
 * (vd ngày đang chỉnh công) — trước đây ô rỗng không hiện ngày nào nên phải chọn lại từng lần.
 * onChange(e) với e.target.value = 'YYYY-MM-DDTHH:mm' khi đã có giờ hợp lệ, '' khi chưa có giờ.
 */
export const DateTimeInput24 = forwardRef(function DateTimeInput24(
  { value, onChange, name, id, defaultDate, className, disabled, readOnly, required, 'aria-label': ariaLabel },
  ref
) {
  const parts = splitDateTime(value);
  const [date, setDate] = useState(parts.date || defaultDate || '');

  // Bên ngoài đặt giá trị mới (nạp form / đổi phiên) → theo ngày của giá trị đó; giá trị rỗng thì
  // quay về ngày mặc định (ngày đang chỉnh) chứ không để ô ngày trống.
  useEffect(() => {
    setDate(parts.date || defaultDate || '');
  }, [parts.date, defaultDate]);

  const emit = (v) => {
    if (typeof onChange === 'function') onChange({ target: { name, id, value: v }, currentTarget: { name, id, value: v } });
  };

  const onDate = (e) => {
    const d = e.target.value;
    setDate(d);
    // Đổi ngày khi đã có giờ → báo mốc mới; chưa có giờ thì chỉ nhớ ngày.
    if (parts.time) emit(joinDateTime(d, parts.time));
  };

  const onTime = (e) => {
    emit(joinDateTime(date || defaultDate || '', e.target.value));
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <input
          ref={ref}
          type="date"
          id={id}
          value={date}
          onChange={onDate}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-label={ariaLabel ? `${ariaLabel} — ngày` : 'Ngày'}
          className={className}
        />
      </div>
      <div className="w-24 flex-shrink-0">
        <TimeInput24
          name={name}
          value={parts.time}
          onChange={onTime}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-label={ariaLabel ? `${ariaLabel} — giờ` : 'Giờ'}
          className={className}
        />
      </div>
    </div>
  );
});

export default TimeInput24;
